import useDeleteOperation from 'api/operation/common/useDeleteOperation';
import useEntryOperationList from 'api/operation/entry/useEntryOperationList';
import useInsertEntryOperation from 'api/operation/entry/useInsertEntryOperation';
import useUpdateEntryOperation from 'api/operation/entry/useUpdateEntryOperation';
import EntryOperationInsertModal from 'components/private/Modals/operation/entry/EntryOperationInsertModal/EntryOperationInsertModal';
import OperationEditModal from 'components/private/Modals/operation/OperationEditModal';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import PageSpinner from 'components/shared/pageSpinner/PageSpinner';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import useEntryOperationTableColumns from 'hooks/operation/useEntryOperationTableColumns';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';

const EntryOperationsPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = useMemo(() => {
    return operationSettings?.defaultZone?.key;
  }, [operationSettings?.defaultZone?.key]);

  const entryPermissions =
    permissions.general.categories.warehouseOperations.entry;

  const canCreate = can(entryPermissions.create);
  const canDelete = can(entryPermissions.delete);
  const canDetail = can(entryPermissions.detail);
  const canEdit = can(entryPermissions.edit);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const { getEntryOperationList, loading, entryOperationList, pageRef } =
    useEntryOperationList();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [deleteChosenEntryOperation, setDeleteChosenEntryOperation] = useState(
    {},
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);
  const [entryOperationEditValues, setEntryOperationEditValues] = useState({});

  const { deleteRequest } = useDeleteOperation();
  const { updateRequest } = useUpdateEntryOperation();
  const { insertRequest } = useInsertEntryOperation();

  const tableRef = useRef(null);

  const fetchList = useCallback(
    ({ page: pageNumber = 1, count = pageSize, term = '' } = {}) => {
      if (!zoneGuid) return;
      getEntryOperationList({
        Guid: zoneGuid,
        Page: pageNumber,
        Count: count,
        Term: term,
      });
    },
    [getEntryOperationList, pageSize, zoneGuid],
  );

  useEffect(() => {
    fetchList({ page: 1, count: pageSize });
  }, []);

  useEffect(() => {
    fetchList({ page, count: pageSize });
  }, [pageSize]);

  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenEntryOperation(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const editItem = useCallback(
    row => () => {
      setEntryOperationEditValues(row);
      setIsEditModalOpen(true);
    },
    [],
  );

  const openItem = useCallback(
    row => () => {
      navigate(`/operation/entry-workspace/${row.guid}`);
    },
    [navigate],
  );

  const [columnsData] = useEntryOperationTableColumns(
    deleteItem,
    editItem,
    openItem,
    tableRef,
    {
      canDetail,
      canEdit,
      canDelete,
    },
  );

  const deleteHandle = guid => {
    setDeleteLoading(true);
    deleteRequest(
      guid,
      () => fetchList({ page, count: pageSize }),
      setIsDeleteModalOpen,
      setDeleteLoading,
    );
  };

  const editHandle = data => {
    if (!data) return;

    setEditLoading(true);
    updateRequest(
      {
        guid: entryOperationEditValues.guid,
        title: data.title,
      },
      () => fetchList({ page, count: pageSize }),
      setIsEditModalOpen,
      setEditLoading,
    );
  };

  const insertHandle = payload => {
    setInsertLoading(true);
    insertRequest(
      () => fetchList({ page, count: pageSize }),
      setIsInsertModalOpen,
      setInsertLoading,
      payload,
    );
  };

  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const closeEditModal = () => setIsEditModalOpen(false);

  function getPageData(pageNumber) {
    setPage(pageNumber);
    fetchList({ page: pageNumber, count: pageSize });
  }

  function handleSearch(text) {
    setPage(1);
    fetchList({ page: 1, count: pageSize, term: text });
  }

  return (
    <RequireOperationSettings>
      <div>{pageRef.refTitle}</div>

      {loading ? (
        <PageSpinner loading />
      ) : (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={entryOperationList?.[page] ?? []}
          rows={entryOperationList?.[page] ?? []}
          pageSize={pageSize}
          disableSelection
          isLoading={loading}
          addLable={canCreate ? 'عملیات ورود جدید' : undefined}
          onAddClick={canCreate ? () => setIsInsertModalOpen(true) : undefined}
          setPageSize={setPageSize}
          apiRef={tableRef}
          onPageChange={getPageData}
          page={page}
          countPages={
            pageRef?.totalCount
              ? Math.ceil(pageRef.totalCount / Number(pageSize))
              : 1
          }
          handleSearchApi={handleSearch}
        />
      )}

      {canDelete ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={closeDeleteModal}
          locationId={deleteChosenEntryOperation.guid}
          title={deleteChosenEntryOperation.title}
          deleteLoading={deleteLoading}
          onDelete={deleteHandle}
        />
      ) : null}

      {canEdit ? (
        <OperationEditModal
          open={isEditModalOpen}
          handleClose={closeEditModal}
          editLoading={editLoading}
          onEdit={editHandle}
          initialValues={entryOperationEditValues}
        />
      ) : null}

      {canCreate ? (
        <EntryOperationInsertModal
          open={isInsertModalOpen}
          handleClose={() => setIsInsertModalOpen(false)}
          insertLoading={insertLoading}
          onInsert={insertHandle}
        />
      ) : null}
    </RequireOperationSettings>
  );
};

export default EntryOperationsPage;
